import React, { useState } from 'react'
import { IAutocompleteBaseOption } from 'transfix-ui/components/AutocompleteBase'
import { INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { ModalBase, IModalBaseProps, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { Typography, makeStyles } from 'transfix-ui/core'
import { ITaskNamespaces, Maybe } from 'types/graphqlTypes'
import { AutocompleteReps } from './AutocompleteReps'

interface IAssignShipmentModalProps extends Omit<IModalBaseProps, 'modalType'> {
  onConfirm: (rep: string | null) => Promise<boolean>
  isLoading: boolean
  errorMessages: string[]
  assignedUserName?: Maybe<string>
  assignedAtTimeAgo?: Maybe<string>
  taskNamespace: ITaskNamespaces
}

const useAssignShipmentModalStyles = makeStyles(theme => ({
  alreadyAssigned: {
    marginBottom: theme.spacing(3),
  },
}))

export const AssignShipmentModal = ({
  assignedAtTimeAgo,
  assignedUserName,
  errorMessages,
  isLoading,
  onClose,
  onConfirm,
  open = false,
  taskNamespace,
  ...props
}: IAssignShipmentModalProps) => {
  const classes = useAssignShipmentModalStyles()
  const isAssigned = !!assignedUserName
  const modalTitle = `${isAssigned ? 'Reassign' : 'Assign'} Shipment to...`
  const confirmationLabel = isAssigned ? 'Reassign Task' : 'Assign Shipment'
  const [selectedRep, setSelectedRep] = useState<string | null>(null)

  const confirmHandler = async () => {
    const isSuccessful = await onConfirm(selectedRep)
    if (isSuccessful) onClose()
  }

  const actions: IModalBaseProps['actions'] = [
    {
      label: 'Cancel',
      action: onClose,
      type: 'dismiss',
      dataTest: 'cancel-button',
      disabled: isLoading,
    },
    {
      label: confirmationLabel,
      action: confirmHandler,
      type: 'confirm',
      dataTest: 'confirm-button',
      disabled: isLoading,
    },
  ]

  const inlineMessageProps = errorMessages.length
    ? {
        message:
          errorMessages.join('\n') ||
          'There was an error assigning this shipment. Please try again.',
        type: INLINE_MESSAGE_VARIANTS.ALERT,
      }
    : undefined

  const changeRepHandler = (newVal: IAutocompleteBaseOption | null) => {
    if (newVal) {
      setSelectedRep(newVal.id)
    } else {
      setSelectedRep(null)
    }
  }

  return (
    <ModalBase
      dataTest='AssignShipmentModal'
      title={modalTitle}
      modalType={MODAL_TYPES.form}
      size={3}
      open={open}
      actions={actions}
      onClose={onClose}
      inlineMessageProps={inlineMessageProps}
      {...props}>
      <div>
        {assignedUserName && (
          <div className={classes.alreadyAssigned}>
            <Typography variant='body2' display='inline'>
              Assigned to {assignedUserName}{' '}
            </Typography>
            <Typography variant='body2' color='textSecondary' display='inline'>
              {assignedAtTimeAgo}
            </Typography>
          </div>
        )}
        <AutocompleteReps
          onChange={changeRepHandler}
          taskNamespace={taskNamespace}
          value={selectedRep || null}
        />
      </div>
    </ModalBase>
  )
}
